import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaTruck,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaBoxes,
  FaUserCheck,
  FaHandsHelping,
  FaStar,
  FaGift,
  FaBolt,
  FaRocket,
  FaClock,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import './DonoDaCarga.css';

import img1 from '../assets/images/motoristaedonodacarga/teladonocarga1.jpg';
import img2 from '../assets/images/motoristaedonodacarga/teladonodacarga2.jpg';
import img3 from '../assets/images/motoristaedonodacarga/teladonodacaraga3.jpg';

const useScrollAnimation = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -30px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);
};

const DonoDaCarga = () => {
  const navigate = useNavigate();

  const [currentSlide, setCurrentSlide] = useState(0);
  const slideIntervalRef = useRef(null);

  const slides = [img1, img2, img3];

  useEffect(() => {
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000);

    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
        slideIntervalRef.current = null;
      }
    };
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
      slideIntervalRef.current = null;
    }
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000);
  };

  const nextSlide = () => goToSlide((currentSlide + 1) % slides.length);
  const prevSlide = () => goToSlide((currentSlide - 1 + slides.length) % slides.length);

  const handleCadastroClick = () => navigate('/cadastro');

  useScrollAnimation();

  return (
    <div className="dono-container">

      {/* ===== SEÇÃO HERO com CARROSSEL ===== */}
      <div className="dono-hero-section">
        <div className="dono-carousel">
          <div
            className="dono-carousel-track"
            style={{
              transform: `translateX(-${currentSlide * (100 / slides.length)}%)`
            }}
          >
            {slides.map((img, idx) => (
              <div className="dono-carousel-slide" key={idx}>
                <img src={img} alt={`Dono da carga ${idx + 1}`} loading="lazy" />
              </div>
            ))}
          </div>

          <button className="dono-carousel-btn prev" onClick={prevSlide} aria-label="Anterior">
            <FaChevronLeft />
          </button>
          <button className="dono-carousel-btn next" onClick={nextSlide} aria-label="Seguinte">
            <FaChevronRight />
          </button>

          <div className="dono-carousel-dots">
            {slides.map((_, idx) => (
              <button
                key={idx}
                className={`dono-dot ${idx === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(idx)}
                aria-label={`Ir para o slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="dono-hero-overlay-light"></div>
        <div className="dono-hero-content">
          <h1 className="hero-title">
            <span>Transporte</span>
            <span>de cargas</span>
            <span>sem complicações</span>
          </h1>
        </div>
      </div>

      {/* ===== INTRODUÇÃO ===== */}
      <div className="dono-intro-section animate-on-scroll">
        <div className="dono-intro-box">
          <FaArrowRight className="intro-icon" />
          <p className="intro-text">
            <strong>Conectamos você aos melhores profissionais</strong> para uma
            logística eficiente e sem preocupações. Seja para uma encomenda
            pequena ou uma carga industrial, temos o veículo certo para si.
          </p>
        </div>
        <p className="intro-sub">
          Com a Carga Fácil, o seu transporte é rápido, seguro e transparente.
        </p>
      </div>

      {/* ===== PORQUÊ ESCOLHER A CARGA FÁCIL ===== */}
      <div className="dono-why-section">
        <h2 className="section-title animate-on-scroll">Porquê escolher a Carga Fácil?</h2>
        <div className="dono-why-grid">
          <div className="why-card animate-on-scroll">
            <FaTruck className="why-icon" />
            <h3>Frota diversificada</h3>
            <p>De motos a camiões pesados, encontre o veículo certo para qualquer tipo de carga.</p>
          </div>
          <div className="why-card animate-on-scroll" style={{ animationDelay: '0.15s' }}>
            <FaMapMarkerAlt className="why-icon" />
            <h3>Rastreamento em tempo real</h3>
            <p>Acompanhe a sua carga do ponto A ao ponto B com atualizações constantes.</p>
          </div>
          <div className="why-card animate-on-scroll" style={{ animationDelay: '0.3s' }}>
            <FaShieldAlt className="why-icon" />
            <h3>Segurança garantida</h3>
            <p>Proteção total desde a coleta até a entrega, com motoristas verificados.</p>
          </div>
          <div className="why-card animate-on-scroll" style={{ animationDelay: '0.45s' }}>
            <FaClock className="why-icon" />
            <h3>Rapidez e eficiência</h3>
            <p>Reduza o tempo de espera e otimize a sua logística com agilidade.</p>
          </div>
        </div>
      </div>

      {/* ===== COMO FUNCIONA ===== */}
      <div className="dono-how-section">
        <h2 className="section-title animate-on-scroll">Como funciona?</h2>
        <div className="dono-steps-grid">
          <div className="step-card animate-on-scroll">
            <div className="step-number">1</div>
            <h3>Cadastre-se</h3>
            <p>Crie a sua conta em poucos minutos e tenha acesso à plataforma.</p>
          </div>
          <div className="step-card animate-on-scroll" style={{ animationDelay: '0.15s' }}>
            <div className="step-number">2</div>
            <h3>Publique a sua carga</h3>
            <p>Descreva o que precisa transportar, com origem, destino e tipo de veículo.</p>
          </div>
          <div className="step-card animate-on-scroll" style={{ animationDelay: '0.3s' }}>
            <div className="step-number">3</div>
            <h3>Receba propostas</h3>
            <p>Motoristas disponíveis farão propostas – escolha a melhor.</p>
          </div>
          <div className="step-card animate-on-scroll" style={{ animationDelay: '0.45s' }}>
            <div className="step-number">4</div>
            <h3>Acompanhe e receba</h3>
            <p>Monitore o trajeto em tempo real e receba a sua carga com total segurança.</p>
          </div>
        </div>
      </div>

      {/* ===== TIPOS DE CARGA ===== */}
      <div className="dono-cargo-section">
        <h2 className="section-title animate-on-scroll">Tipos de carga que transportamos</h2>
        <div className="dono-cargo-grid">
          <div className="cargo-card animate-on-scroll">
            <FaBoxes className="cargo-icon" />
            <h3>Pequenas encomendas</h3>
            <p>Documentos, compras, caixas leves – ideal para motos e txopelas.</p>
          </div>
          <div className="cargo-card animate-on-scroll" style={{ animationDelay: '0.15s' }}>
            <FaBoxes className="cargo-icon" />
            <h3>Carga média</h3>
            <p>Móveis, eletrodomésticos, materiais de construção – vans e pickups.</p>
          </div>
          <div className="cargo-card animate-on-scroll" style={{ animationDelay: '0.3s' }}>
            <FaBoxes className="cargo-icon" />
            <h3>Carga pesada</h3>
            <p>Materiais industriais, grandes volumes – camiões de médio e grande porte.</p>
          </div>
        </div>
      </div>

      {/* ===== BENEFÍCIOS PARA CLIENTES PIONEIROS ===== */}
      <div className="dono-pioneer-section animate-on-scroll">
        <div className="pioneer-content">
          <FaRocket className="pioneer-rocket" />
          <div className="pioneer-text">
            <h2>Seja um dos primeiros 50 clientes!</h2>
            <p>
              <strong>Cadastre-se agora</strong> e garanta o seu lugar entre os clientes pioneiros.
              Quando o aplicativo Carga Fácil for lançado na <strong>Google Play Store</strong> e na
              <strong> Apple App Store</strong>, você será avisado em primeira mão e terá
              <strong> 15 dias de uso completamente gratuito</strong> para experimentar todos os benefícios.
            </p>
            <ul className="pioneer-list">
              <li>
                <FaGift className="list-icon" />
                <strong>15 dias grátis</strong> após o lançamento do app
              </li>
              <li>
                <FaBolt className="list-icon" />
                Aviso prioritário sobre a disponibilidade do app
              </li>
              <li>
                <FaStar className="list-icon" />
                Distintivo exclusivo de <strong>&quot;Cliente Embaixador&quot;</strong> no perfil
              </li>
              <li>
                <FaGift className="list-icon" />
                Acesso antecipado a novas funcionalidades
              </li>
            </ul>
            <div className="pioneer-actions">
              <button className="pioneer-btn" onClick={handleCadastroClick}>
                Cadastrar-me agora
              </button>
              {/* ← CONTACTOS REMOVIDOS */}
            </div>
          </div>
        </div>
      </div>

      {/* ===== SERVIÇOS COMPLEMENTARES ===== */}
      <div className="dono-services-section">
        <h2 className="section-title animate-on-scroll">Serviços complementares</h2>
        <div className="dono-services-grid">
          <div className="service-card animate-on-scroll">
            <FaHandsHelping className="service-icon" />
            <h3>Auxiliares de carga</h3>
            <p>Profissionais para carregamento e descarregamento seguro.</p>
          </div>
          <div className="service-card animate-on-scroll" style={{ animationDelay: '0.15s' }}>
            <FaBoxes className="service-icon" />
            <h3>Manuseio especializado</h3>
            <p>Cuidado profissional com cargas frágeis ou de alto valor.</p>
          </div>
          <div className="service-card animate-on-scroll" style={{ animationDelay: '0.3s' }}>
            <FaUserCheck className="service-icon" />
            <h3>Supervisão</h3>
            <p>Acompanhamento em tempo real de todas as etapas.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonoDaCarga;